import Axios from '@/utils/axios';

// ==================== Fleet Snapshot ====================
export interface FleetSnapshotResponse {
  status: number;
  data: {
    totalVehicles: number;
    operationalUtilization: number;
    fleetHealthScore: number;
    activeRepoCases: number;
  };
}

export const getFleetSnapshot = async (): Promise<FleetSnapshotResponse> => {
  try {
    const response = await Axios.get('/v1/fleet/dashboard/fleet-snapshot');
    return response.data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch fleet snapshot: ${error.message}`);
    }
    throw new Error('Failed to fetch fleet snapshot');
  }
};

// ==================== Utilization Trend ====================
export interface UtilizationTrendRequest {
  days?: number;
}

interface UtilizationDataPoint {
  date: string;
  utilization: number;
  activeVehicles: number;
  operationalVehicles: number;
  trend?: number;
}

interface UtilizationSummary {
  average: number;
  highest: number;
  lowest: number;
}

export interface UtilizationTrendResponse {
  status: number;
  data: {
    dataPoints: UtilizationDataPoint[];
    summary: UtilizationSummary;
  };
}

export const getUtilizationTrend = async (
  params?: UtilizationTrendRequest
): Promise<UtilizationTrendResponse> => {
  try {
    const response = await Axios.get('/v1/fleet/dashboard/utilization-trend', {
      params,
    });
    return response.data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch utilization trend: ${error.message}`);
    }
    throw new Error('Failed to fetch utilization trend');
  }
};

// ==================== OEM Distribution ====================
export interface OEMDistributionRequest {
  page?: number;
  size?: number;
}

export interface OEMDistributionItem {
  oemName: string;
  totalVehicle: number;
  percentageOfTotal: number;
  active: number;
  idle: number;
  inRepair: number;
  broken: number;
  lost: number;
}

export interface OEMDistributionResponse {
  status: number;
  data: {
    items?: OEMDistributionItem[];
  } | OEMDistributionItem[];
  pagination?: {
    page: number;
    size: number;
    total: number;
    lastPage: number;
  };
}

export const getOEMDistribution = async (
  params?: OEMDistributionRequest
): Promise<OEMDistributionResponse> => {
  try {
    const response = await Axios.get('/v1/fleet/dashboard/oem-distribution/detail', {
      params,
    });
    return response.data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch OEM distribution: ${error.message}`);
    }
    throw new Error('Failed to fetch OEM distribution');
  }
};

// ==================== City Distribution ====================
export interface CityDistributionRequest {
  page?: number;
  size?: number;
}

export interface CityDistributionItem {
  city: string;
  totalVehicle: number;
  percentageOfTotal: number;
  growthMoM: number;
  top3Clients: string[];
}

export interface CityDistributionResponse {
  status: number;
  data: {
    items?: CityDistributionItem[];
  } | CityDistributionItem[];
  pagination?: {
    page: number;
    size: number;
    total: number;
    lastPage: number;
  };
}

export const getCityDistribution = async (
  params?: CityDistributionRequest
): Promise<CityDistributionResponse> => {
  try {
    const response = await Axios.get('/v1/fleet/dashboard/city-distribution/detail', {
      params,
    });
    return response.data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch city distribution: ${error.message}`);
    }
    throw new Error('Failed to fetch city distribution');
  }
};

// ==================== Client Distribution ====================
export interface ClientDistributionRequest {
  page?: number;
  size?: number;
}

export interface ClientDistributionItem {
  clientName: string;
  hubName: string;
  totalVehicle: number;
  percentageOfTotal: number;
}

export interface ClientDistributionResponse {
  status: number;
  data: {
    items?: ClientDistributionItem[];
  } | ClientDistributionItem[];
  pagination?: {
    page: number;
    size: number;
    total: number;
    lastPage: number;
  };
}

export const getClientDistribution = async (
  params?: ClientDistributionRequest
): Promise<ClientDistributionResponse> => {
  try {
    const response = await Axios.get('/v1/fleet/dashboard/client-hub-distribution/detail', {
      params,
    });
    return response.data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch client distribution: ${error.message}`);
    }
    throw new Error('Failed to fetch client distribution');
  }
};

// ==================== Hub Location Distribution ====================
export interface HubLocationDistributionRequest {
  page?: number;
  size?: number;
}

export interface HubLocationDistributionItem {
  hubID: number;
  hubName: string;
  totalVehicle: number;
  percentageOfTotal: number;
  active: number;
  idle: number;
  inRepair: number;
  broken: number;
  lost: number;
}

export interface HubLocationDistributionResponse {
  status: number;
  data: {
    items?: HubLocationDistributionItem[];
  } | HubLocationDistributionItem[];
  pagination?: {
    page: number;
    size: number;
    total: number;
    lastPage: number;
  };
}

export const getHubLocationDistribution = async (
  params?: HubLocationDistributionRequest
): Promise<HubLocationDistributionResponse> => {
  try {
    const response = await Axios.get('/v1/fleet/dashboard/hub-location/detail', {
      params,
    });
    return response.data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch hub location distribution: ${error.message}`);
    }
    throw new Error('Failed to fetch hub location distribution');
  }
};

// ==================== Chart Data (Simplified) ====================
// Generic response for chart endpoints - keeps it simple
export interface ChartDataResponse<T = any> {
  status: number;
  data: T[];
}

// Simple wrappers for chart-specific endpoints
export const getOEMChart = async (): Promise<ChartDataResponse> => {
  const response = await Axios.get('/v1/fleet/dashboard/oem-distribution/chart');
  return response.data;
};

export const getCityChart = async (): Promise<ChartDataResponse> => {
  const response = await Axios.get('/v1/fleet/dashboard/city-distribution/chart');
  return response.data;
};

export const getClientChart = async (): Promise<ChartDataResponse> => {
  const response = await Axios.get('/v1/fleet/dashboard/client-hub-distribution/chart');
  return response.data;
};

export const getHubLocationChart = async (): Promise<ChartDataResponse> => {
  const response = await Axios.get('/v1/fleet/dashboard/hub-location/chart');
  return response.data;
};

// ==================== Business Unit Distribution ====================
export interface BusinessUnitDistributionItem {
  businessUnit: string;
  totalVehicle: number;
  percentageOfTotal: number;
  active: number;
  idle: number;
  inRepair: number;
  broken: number;
  lost: number;
}

export const getBusinessUnitChart = async (): Promise<ChartDataResponse<BusinessUnitDistributionItem>> => {
  const response = await Axios.get('/v1/fleet/dashboard/business-unit-distribution/chart');
  return response.data;
};

